import { Dispatch } from 'redux';

import { get, post, RequestExceptionWithResponse } from '@eika-infrastruktur/request';

import customHistory from '../customHistory';
import {
    InterestsAndFeesWeb,
    LoanApplicationResponse,
    LoanProduct,
    ObjectCalculatorDataWebResponse,
    ResponseWithError,
    UpgradeToVehicleLoanRequest,
} from '../generated-types';
import { AppState } from '../reducers';
import { customFailed } from '../util/common';
import LoanProductVehicleTypeMap from '../util/mappings/loanProductVehicleTypeMap';
import routePath from '../util/mappings/routePath';
import { trackEvent, TrackingStep } from '../util/piwik-utils';
import { FormTypeCalculatorVehicleInfo } from './forms';
import { getCalculatorRequestPath, getRequestHeaders } from './tools';
import { actionTypes, AppActions } from './types';
import { fetchVehicleInfoFailed, fetchVehicleInfoFulfilled, fetchVehicleInfoStarted } from './vehicle';

type Action<T> = import('./types').Action<T>;
type OngoingApplicationResponse = import('../generated-types').CustomerCaseInfoLandingPage[];
type FetchVehicleInfoResponse = import('../generated-types').VehicleDetailsWebResponse;
type AppError = import('../generated-types').AppError;

export interface FetchOngoingApplicationStartedAction extends Action<
    typeof actionTypes.FETCH_ONGOING_APPLICATIONS_STARTED
> {
    type: typeof actionTypes.FETCH_ONGOING_APPLICATIONS_STARTED;
}

export interface FetchOngoingApplicationFulfilledAction extends Action<
    typeof actionTypes.FETCH_ONGOING_APPLICATIONS_FULFILLED
> {
    type: typeof actionTypes.FETCH_ONGOING_APPLICATIONS_FULFILLED;
    payload: OngoingApplicationResponse;
}

export interface FetchOngoingApplicationFailedAction extends Action<
    typeof actionTypes.FETCH_ONGOING_APPLICATIONS_FAILED
> {
    type: typeof actionTypes.FETCH_ONGOING_APPLICATIONS_FAILED;
    payload: RequestExceptionWithResponse;
}

export interface FetchOngoingApplicationEuroTaxFulfilled extends Action<
    typeof actionTypes.FETCH_ONGOING_APPLICATIONS_EUROTAX_FULFILLED
> {
    type: typeof actionTypes.FETCH_ONGOING_APPLICATIONS_EUROTAX_FULFILLED;
    applicationId: string;
}

export interface FetchOngoingApplicationEuroTaxCleared extends Action<
    typeof actionTypes.FETCH_ONGOING_APPLICATIONS_EUROTAX_CLEAR
> {
    type: typeof actionTypes.FETCH_ONGOING_APPLICATIONS_EUROTAX_CLEAR;
    applicationId: string;
}

export interface UpgradeOngoingApplicationStartedAction extends Action<
    typeof actionTypes.UPGRADE_ONGOING_APPLICATION_STARTED
> {
    type: typeof actionTypes.UPGRADE_ONGOING_APPLICATION_STARTED;
}

export interface UpgradeOngoingApplicationFailedAction extends Action<
    typeof actionTypes.UPGRADE_ONGOING_APPLICATION_FAILED
> {
    type: typeof actionTypes.UPGRADE_ONGOING_APPLICATION_FAILED;
    payload: RequestExceptionWithResponse;
    applicationId: string;
}

export interface UpgradeOngoingApplicationSuccessAction extends Action<
    typeof actionTypes.UPGRADE_ONGOING_APPLICATION_SUCCESS
> {
    type: typeof actionTypes.UPGRADE_ONGOING_APPLICATION_SUCCESS;
}

export interface FetchOngoingApplicationEuroTaxFailed extends Action<
    typeof actionTypes.FETCH_ONGOING_APPLICATIONS_EUROTAX_FAILED
> {
    type: typeof actionTypes.FETCH_ONGOING_APPLICATIONS_EUROTAX_FAILED;
    payload: RequestExceptionWithResponse;
    applicationId: string;
}

export interface FetchObjectCalculatorDataFulfilledAction extends Action<
    typeof actionTypes.FETCH_OBJECT_CALCULATOR_DATA_FULFILLED
> {
    type: typeof actionTypes.FETCH_OBJECT_CALCULATOR_DATA_FULFILLED;
    interestsAndFees: InterestsAndFeesWeb | null;
}

export type FetchOngoingApplicationActions =
    | FetchOngoingApplicationStartedAction
    | FetchOngoingApplicationFulfilledAction
    | FetchOngoingApplicationFailedAction
    | FetchOngoingApplicationEuroTaxFulfilled
    | UpgradeOngoingApplicationStartedAction
    | FetchOngoingApplicationEuroTaxCleared
    | UpgradeOngoingApplicationFailedAction
    | UpgradeOngoingApplicationSuccessAction
    | FetchObjectCalculatorDataFulfilledAction
    | FetchOngoingApplicationEuroTaxFailed;

export const fetchOngoingApplicationStarted = (): FetchOngoingApplicationStartedAction => ({
    type: actionTypes.FETCH_ONGOING_APPLICATIONS_STARTED,
});
export const fetchOngoingApplicationFulfilled = (
    json: OngoingApplicationResponse,
): FetchOngoingApplicationFulfilledAction => ({
    type: actionTypes.FETCH_ONGOING_APPLICATIONS_FULFILLED,
    payload: json,
});
export const fetchOngoingApplicationFailed = (
    error: RequestExceptionWithResponse,
): FetchOngoingApplicationFailedAction => ({
    type: actionTypes.FETCH_ONGOING_APPLICATIONS_FAILED,
    payload: error,
});

export const fetchOngoingApplicationEuroTaxFailed = (
    error: RequestExceptionWithResponse,
    applicationId: string,
): FetchOngoingApplicationEuroTaxFailed => ({
    type: actionTypes.FETCH_ONGOING_APPLICATIONS_EUROTAX_FAILED,
    payload: error,
    applicationId,
});

export const fetchOngoingApplicationEuroTaxFulfilled = (
    applicationId: string,
): FetchOngoingApplicationEuroTaxFulfilled => ({
    type: actionTypes.FETCH_ONGOING_APPLICATIONS_EUROTAX_FULFILLED,
    applicationId,
});

export const fetchOngoingApplicationEuroTaxCleared = (
    applicationId: string,
): FetchOngoingApplicationEuroTaxCleared => ({
    type: actionTypes.FETCH_ONGOING_APPLICATIONS_EUROTAX_CLEAR,
    applicationId,
});

export const fetchUpgradeApplicationStarted = (): UpgradeOngoingApplicationStartedAction => ({
    type: actionTypes.UPGRADE_ONGOING_APPLICATION_STARTED,
});

export const fetchUpgradeApplicationSuccess = (): UpgradeOngoingApplicationSuccessAction => ({
    type: actionTypes.UPGRADE_ONGOING_APPLICATION_SUCCESS,
});

export const fetchUpgradeApplicationFailed = (
    applicationId: string,
    error: RequestExceptionWithResponse,
): UpgradeOngoingApplicationFailedAction => ({
    type: actionTypes.UPGRADE_ONGOING_APPLICATION_FAILED,
    applicationId,
    payload: error,
});

export const fetchObjectCalculatorDataFulfilledAction = (
    response: ObjectCalculatorDataWebResponse | null,
): FetchObjectCalculatorDataFulfilledAction => ({
    type: actionTypes.FETCH_OBJECT_CALCULATOR_DATA_FULFILLED,
    interestsAndFees: response ? response.interestsAndFees : null,
});

export const fetchOngoingApplications = () => async (dispatch: Dispatch<AppActions>, getState: () => AppState) => {
    const { loader, context } = getState();
    const { isFetchingOngoingApplications } = loader;
    if (!isFetchingOngoingApplications) {
        trackEvent(TrackingStep.OngoingApplications);
        try {
            dispatch(fetchOngoingApplicationStarted());
            const response: OngoingApplicationResponse = await get(
                `${actionTypes.RESOURCE_PATH}/landing-page`,
                getRequestHeaders(context.config.bankregnr),
            );
            dispatch(fetchOngoingApplicationFulfilled(response));
        } catch (error) {
            dispatch(fetchOngoingApplicationFailed(error));
        }
    }
};

export const fetchOngoingApplicationEuroTax =
    (form: FormTypeCalculatorVehicleInfo, applicationId: string) =>
    async (dispatch: Dispatch<AppActions>, getState: () => AppState): Promise<void> => {
        const { loader, context } = getState();
        const { vehicleRegistrationNumber, odometerValue } = form;
        const { isFetchingVehicleInfo } = loader;

        const data = {
            vehicleRegistrationNumber,
            odometerValue: odometerValue !== null ? odometerValue : null,
        };

        if (!isFetchingVehicleInfo) {
            try {
                dispatch(fetchVehicleInfoStarted());
                const response: ResponseWithError<FetchVehicleInfoResponse> = await post(
                    `${actionTypes.RESOURCE_PATH}/vehicle/details`,
                    data,
                    getRequestHeaders(context.config.bankregnr),
                );
                if (response.failure) {
                    dispatch(fetchVehicleInfoFailed(customFailed(response.errorOrNull || '')));
                    dispatch(
                        fetchOngoingApplicationEuroTaxFailed(customFailed(response.errorOrNull || ''), applicationId),
                    );
                } else {
                    dispatch(fetchVehicleInfoFulfilled(response.responseOrNull || undefined));
                    dispatch(fetchOngoingApplicationEuroTaxFulfilled(applicationId));
                }
            } catch (error) {
                dispatch(fetchVehicleInfoFailed(error));
            }
        }
    };

export const fetchEuroTaxLoanCommitment =
    (applicationId: string, payload: UpgradeToVehicleLoanRequest) =>
    async (dispatch: Dispatch<AppActions>, getState: () => AppState): Promise<void> => {
        const { loader, context } = getState();
        const { isUpgradingApplication } = loader;

        if (!isUpgradingApplication) {
            try {
                dispatch(fetchUpgradeApplicationStarted());
                const response: ResponseWithError<LoanApplicationResponse> = await post(
                    `${actionTypes.RESOURCE_PATH}/application/loan-commitment-to-loan-upgrade`,
                    payload,
                    getRequestHeaders(context.config.bankregnr),
                );

                if (response.failure) {
                    dispatch(fetchUpgradeApplicationFailed(applicationId, customFailed(response.errorOrNull || '')));
                } else {
                    dispatch(fetchUpgradeApplicationSuccess());
                    const path = routePath.loanUpgradeSuccess.replace(':appId', applicationId);
                    customHistory.push(path);
                }
            } catch (error) {
                dispatch(fetchUpgradeApplicationFailed(applicationId, error));
            }
        }
    };

export const fetchObjectCalculatorDataAction =
    (loanProduct: LoanProduct) =>
    async (dispatch: Dispatch<AppActions>, getState: () => AppState): Promise<void> => {
        const { context } = getState();
        const productVehicleMap = LoanProductVehicleTypeMap();
        let mappedProduct = productVehicleMap.get(loanProduct);
        mappedProduct = typeof mappedProduct !== 'undefined' ? mappedProduct : ('UNKNOWN_PRODUCT' as AppError);

        try {
            const response: ObjectCalculatorDataWebResponse = await get(
                getCalculatorRequestPath(context.config.bankregnr, mappedProduct),
                getRequestHeaders(context.config.bankregnr),
            );

            if (response) {
                dispatch(fetchObjectCalculatorDataFulfilledAction(response));
            }
        } catch {
            //  dispatch(fetchObjectCalculatorDataFulfilledAction(response));
        }
    };
